/* ============================================================
   hyve-motion.js · v1 (Phase 1 — global layer)
   One small, dependency-free script that powers every hyve
   behavior sitewide. Loaded with defer from theme.liquid.

   What it drives (all opt-in via data attributes, all no-ops
   when the attribute isn't on the page):
   - [data-reveal]            reveal-on-scroll, fire once, staggered
   - .hyve-hdr                header solid-on-scroll state
   - video[data-hyve-autoplay] play when in view, pause when not
   - [data-hyve-scroll]       arrow buttons for horizontal card rows
   - [data-hyve-acc]          FAQ-style accordion items
   Respects prefers-reduced-motion throughout, and re-initializes
   on Shopify theme-editor section reloads.
   ============================================================ */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- reveal-on-scroll ---------- */
  function initReveal(root) {
    var els = (root || document).querySelectorAll('[data-reveal]:not(.is-in)');
    if (!els.length) return;
    if (reduced || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        obs.unobserve(e.target); // fire once — never re-animate on scroll-back
      });
    }, { threshold: 0.18 });

    els.forEach(function (el) {
      var parent = el.parentElement;
      var sibs = parent
        ? Array.prototype.filter.call(parent.children, function (c) { return c.hasAttribute('data-reveal'); })
        : [el];
      el.style.setProperty('--hyve-i', Math.max(0, sibs.indexOf(el)));
      io.observe(el);
    });
  }

  /* ---------- header solid-on-scroll ---------- */
  function initHeader() {
    var hdr = document.querySelector('.hyve-hdr');
    if (!hdr) return;
    var ticking = false;
    function check() {
      ticking = false;
      var y = window.scrollY || 0;
      hdr.classList.toggle('is-solid', y > 60);
      hdr.classList.toggle('at-top', y < 40);
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(check);
    }, { passive: true });
    check();
  }

  /* ---------- autoplay-on-view videos ---------- */
  function initVideos(root) {
    var vids = (root || document).querySelectorAll('video[data-hyve-autoplay]');
    if (!vids.length || reduced || !('IntersectionObserver' in window)) return;
    vids.forEach(function (v) {
      v.muted = true;
      v.setAttribute('playsinline', '');
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { v.play().catch(function () {}); }
          else { v.pause(); }
        });
      }, { threshold: 0.35 });
      io.observe(v);
    });
  }

  /* ---------- horizontal-row arrows ---------- */
  function initScrollers(root) {
    (root || document).querySelectorAll('[data-hyve-scroll]').forEach(function (btn) {
      if (btn.dataset.hyveBound) return;
      btn.dataset.hyveBound = '1';
      btn.addEventListener('click', function () {
        var row = document.getElementById(btn.getAttribute('data-hyve-scroll'));
        if (!row) return;
        var dir = parseInt(btn.getAttribute('data-hyve-dir') || '1', 10);
        row.scrollBy({
          left: dir * Math.min(row.clientWidth * 0.8, 360),
          behavior: reduced ? 'auto' : 'smooth'
        });
      });
    });
  }

  /* ---------- accordion ---------- */
  function initAccordions(root) {
    (root || document).querySelectorAll('[data-hyve-acc]').forEach(function (item) {
      if (item.dataset.hyveBound) return;
      item.dataset.hyveBound = '1';
      var q = item.querySelector('.hyve-acc-q');
      var a = item.querySelector('.hyve-acc-a');
      if (!q || !a) return;
      q.addEventListener('click', function () {
        var open = item.classList.toggle('open');
        q.setAttribute('aria-expanded', open ? 'true' : 'false');
        a.style.maxHeight = open ? a.scrollHeight + 'px' : '0px';
      });
    });
  }

  function initAll(root) {
    initReveal(root);
    initVideos(root);
    initScrollers(root);
    initAccordions(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); initHeader(); });
  } else {
    initAll(); initHeader();
  }

  /* Theme editor: re-init when a section is (re)loaded */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
    initHeader();
  });
})();